/**
 * Core-100 R4 wave 3 (X4) — CORE-AE F: the Command Center's Location & custody section.
 *
 * Composed from the two authorities, owned by neither (CORE-AE: "composing is not owning"):
 *   - location truth (CORE-X / CORE-AO) through `get_vehicle_location_history` (the server applies the ticket's
 *     visibility rule, R4-14), plus the location PLAN as a separate, clearly-labelled answer;
 *   - custody (CORE-AK) through `ticket_custody` and `ticket_custody_events`.
 * Every fact says how it is known (`locationProvenance`; a custody event names who recorded it). A section that
 * cannot be read says so — an error is never shown as "nothing here" (CORE-AE GAP-003). Read-only: moving the
 * vehicle and handing custody over stay with their owners' controls.
 */
import { useQuery } from '@tanstack/react-query';
import { MapPin, ArrowLeftRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { readLocationHistory, readLocationPlan } from '@/lib/location/api';
import { useTicketCustody } from '@/hooks/tickets/useTicketCustody';
import { useTicketCustodyHistory } from '@/hooks/tickets/useTicketCustodyHistory';
import { useCustodyActorNames } from '@/hooks/tickets/useCustodyActorNames';
import { custodyActorDisplay } from '@/lib/tickets/lifecycle/custodyHistory';
import {
  custodyNowWords,
  locationPlaceWords,
  locationProvenance,
  movementAttribution,
  movementOriginWords,
  type LocationHistoryRow,
} from '@/lib/tickets/locationCustodyComposition';
import { formatShopTime } from '@/utils/formatShopTime';

const EVIDENCE_STYLE: Record<string, string> = {
  person: 'bg-primary/10 text-primary border-primary/30',
  workflow: 'bg-muted text-muted-foreground',
  sensor: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  inferred: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  unknown: 'bg-muted text-muted-foreground',
};

function ReadFailure({ what }: { what: string }) {
  return (
    <p className="text-sm text-destructive" data-testid="location-custody-read-failed">
      {what} could not be read. This is an error, not an empty record.
    </p>
  );
}

export function TicketLocationCustodyPanel({
  ticketId, shopId, timeZone, className,
}: { ticketId: string; shopId: string | null; timeZone: string; className?: string }) {
  const history = useQuery({
    queryKey: ['ticket-location-history', ticketId],
    queryFn: async () => (await readLocationHistory(ticketId, 20)) as unknown as LocationHistoryRow[],
    retry: false,
  });
  const plan = useQuery({
    queryKey: ['ticket-location-plan', ticketId],
    queryFn: () => readLocationPlan(ticketId),
    retry: false,
  });
  const custody = useTicketCustody(ticketId);
  const custodyEvents = useTicketCustodyHistory(ticketId, true);
  const actorIds = [
    ...(custodyEvents.data ?? []).map((e) => e.actorId),
    ...(history.data ?? []).map((r) => r.actor_user_id),
    ...(history.data ?? []).map((r) => r.moved_by_user_id ?? null),
  ];
  const names = useCustodyActorNames(actorIds, shopId);
  const at = (iso: string) => formatShopTime(new Date(iso), timeZone, 'short');

  const rows = history.data ?? [];
  const current = rows.find((r) => r.is_current) ?? null;
  const earlier = rows.filter((r) => !r.is_current).slice(0, 5);
  const events = [...(custodyEvents.data ?? [])].reverse().slice(0, 6);

  return (
    <ScrollArea className={className}>
      <div className="px-6 py-4 space-y-6" data-testid="command-center-location-custody">
        <section className="space-y-3" aria-label="Where the vehicle is">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-2">
            <MapPin className="h-4 w-4" /> Where the vehicle is
          </h3>
          {history.isError ? <ReadFailure what="The vehicle's location" /> : history.isLoading ? (
            <p className="text-sm text-muted-foreground">Reading…</p>
          ) : current ? (
            <LocationFact row={current} at={at} names={names.data ?? {}} testId="location-current" />
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="location-current-none">
              No location has been recorded for this visit.
            </p>
          )}
          {plan.isError ? <ReadFailure what="The location plan" /> : plan.data ? (
            <p className="text-xs text-muted-foreground" data-testid="location-plan">
              Planned: {plan.data.bayId ? 'a bay' : plan.data.parkingSpotId ? 'a parking spot' : plan.data.kind}
              {' '}— a plan, not where the vehicle is.
            </p>
          ) : null}
          {earlier.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground">Before that</div>
              {earlier.map((r) => (
                <LocationFact key={r.event_id} row={r} at={at} names={names.data ?? {}} compact />
              ))}
            </div>
          )}
        </section>

        <section className="space-y-3" aria-label="Who has the visit">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-2">
            <ArrowLeftRight className="h-4 w-4" /> Who has the visit
          </h3>
          {custody.isError ? <ReadFailure what="Custody" /> : custody.isLoading ? (
            <p className="text-sm text-muted-foreground">Reading…</p>
          ) : (
            <div className="bg-muted/30 rounded-lg p-3 text-sm" data-testid="custody-current"
              data-custody-owner={custody.data?.custody_owner ?? 'none'}>
              <div className="font-medium">{custodyNowWords(custody.data ?? null)}</div>
              {custody.data && (
                <div className="text-xs text-muted-foreground">
                  Since {at(custody.data.entered_at)} · recorded by the custody authority
                </div>
              )}
            </div>
          )}
          {custodyEvents.isError ? <ReadFailure what="The custody history" /> : events.length > 0 && (
            <ul className="space-y-1 text-xs" data-testid="custody-events">
              {events.map((e) => {
                const who = custodyActorDisplay(e.actorId, e.actorRole, names.data ?? {});
                return (
                  <li key={e.eventId ?? `${e.eventKind}-${e.createdAt}`} className="flex flex-wrap gap-x-2">
                    <span className="font-medium">{e.label}</span>
                    <span className="text-muted-foreground">
                      {at(e.createdAt)} · by {who.primary}{who.role ? ` (${who.role})` : ''}{e.reason ? ` · ${e.reason}` : ''}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </ScrollArea>
  );
}

function LocationFact({
  row, at, names, compact = false, testId,
}: { row: LocationHistoryRow; at: (iso: string) => string; names: Record<string, string>; compact?: boolean; testId?: string }) {
  const prov = locationProvenance(row);
  // L5: the physical mover only when attested; the recorder always; never an inferred mover.
  const attribution = movementAttribution(row, names);
  const origin = movementOriginWords(row);
  return (
    <div className={compact ? 'text-xs flex flex-wrap items-center gap-2' : 'bg-muted/30 rounded-lg p-3 space-y-1'}
      data-testid={testId} data-location-kind={row.location_kind}>
      <span className={compact ? '' : 'font-medium'}>{locationPlaceWords(row)}</span>
      <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="outline" className={`text-[10px] font-normal px-1.5 py-0 ${EVIDENCE_STYLE[prov.evidence]}`}
          data-location-evidence={prov.evidence} data-location-source={row.source}>
          {prov.words}
        </Badge>
        <span>{at(row.observed_at)}{origin ? ` · ${origin}` : ''}{row.reason ? ` · ${row.reason}` : ''}</span>
      </span>
      <span className="block w-full text-xs text-muted-foreground" data-testid="location-attribution"
        data-movement-attribution={attribution.key} data-mover-confirmed={attribution.moverConfirmed ? 'yes' : 'no'}>
        <span className="font-medium">{attribution.label}:</span> {attribution.sentence}
      </span>
    </div>
  );
}
